import { useEffect, useMemo, useState } from 'react';
import { ScrapersDto, useGetCampaignSourcesQuery } from '@api/apiCompat';

// Mock scrapers data based on campaign type (lead generation focused)
// All scrapers produce contact data: name, email, phone
const MOCK_SCRAPERS: Record<number, ScrapersDto[]> = {
  // Local Search - business contacts from directories
  1: [
    {
      scrapers_id: 1,
      scraper_id: 1,
      scraper_name: 'Google Maps',
      site: 'google.com/maps',
      price: 0.5,
      campaign_type_id_campaign_type: 1,
    } as ScrapersDto,
    {
      scrapers_id: 2,
      scraper_id: 2,
      scraper_name: 'Yelp',
      site: 'yelp.com',
      price: 0.5,
      campaign_type_id_campaign_type: 1,
    } as ScrapersDto,
    {
      scrapers_id: 13,
      scraper_id: 13,
      scraper_name: 'Yellow Pages',
      site: 'yellowpages.com',
      price: 0.5,
      campaign_type_id_campaign_type: 1,
    } as ScrapersDto,
    {
      scrapers_id: 14,
      scraper_id: 14,
      scraper_name: 'Bing Places',
      site: 'bing.com/maps',
      price: 0.5,
      campaign_type_id_campaign_type: 1,
    } as ScrapersDto,
  ],
  // Legal/Professional Services - law, lawyer, attorney
  6: [
    {
      scrapers_id: 15,
      scraper_id: 15,
      scraper_name: 'FindLaw',
      site: 'lawyers.findlaw.com',
      price: 0.5,
      campaign_type_id_campaign_type: 6,
    } as ScrapersDto,
  ],
  // SaaS/Software - saas, software
  7: [
    {
      scrapers_id: 16,
      scraper_id: 16,
      scraper_name: 'G2',
      site: 'g2.com',
      price: 0.5,
      campaign_type_id_campaign_type: 7,
    } as ScrapersDto,
    {
      scrapers_id: 17,
      scraper_id: 17,
      scraper_name: 'Capterra',
      site: 'capterra.com',
      price: 0.5,
      campaign_type_id_campaign_type: 7,
    } as ScrapersDto,
  ],
  // Industrial/Manufacturing - industrial, machine equipment
  8: [
    {
      scrapers_id: 18,
      scraper_id: 18,
      scraper_name: 'Thomasnet',
      site: 'thomasnet.com',
      price: 0.5,
      campaign_type_id_campaign_type: 8,
    } as ScrapersDto,
  ],
  // E-commerce removed - doesn't produce lead contact data (name, email, phone)
  // Real Estate - agent/broker contacts
  3: [
    {
      scrapers_id: 5,
      scraper_id: 5,
      scraper_name: 'Zillow',
      site: 'zillow.com',
      price: 0.5,
      campaign_type_id_campaign_type: 3,
    } as ScrapersDto,
    {
      scrapers_id: 6,
      scraper_id: 6,
      scraper_name: 'Realtor.com',
      site: 'realtor.com',
      price: 0.5,
      campaign_type_id_campaign_type: 3,
    } as ScrapersDto,
    {
      scrapers_id: 7,
      scraper_id: 7,
      scraper_name: 'Realtyhop',
      site: 'realtyhop.com',
      price: 0.5,
      campaign_type_id_campaign_type: 3,
    } as ScrapersDto,
    {
      scrapers_id: 8,
      scraper_id: 8,
      scraper_name: 'Renthop',
      site: 'renthop.com',
      price: 0.5,
      campaign_type_id_campaign_type: 3,
    } as ScrapersDto,
  ],
  // Job Sites
  4: [
    {
      scrapers_id: 9,
      scraper_id: 9,
      scraper_name: 'Indeed',
      site: 'indeed.com',
      price: 0.5,
      campaign_type_id_campaign_type: 4,
    } as ScrapersDto,
    {
      scrapers_id: 10,
      scraper_id: 10,
      scraper_name: 'LinkedIn Jobs',
      site: 'linkedin.com/jobs',
      price: 0.5,
      campaign_type_id_campaign_type: 4,
    } as ScrapersDto,
  ],
  // Social Media
  5: [
    {
      scrapers_id: 11,
      scraper_id: 11,
      scraper_name: 'LinkedIn',
      site: 'linkedin.com',
      price: 0.5,
      campaign_type_id_campaign_type: 5,
    } as ScrapersDto,
    {
      scrapers_id: 12,
      scraper_id: 12,
      scraper_name: 'Facebook Pages',
      site: 'facebook.com',
      price: 0.5,
      campaign_type_id_campaign_type: 5,
    } as ScrapersDto,
    {
      scrapers_id: 19,
      scraper_id: 19,
      scraper_name: 'Instagram Business',
      site: 'instagram.com',
      price: 0.5,
      campaign_type_id_campaign_type: 5,
    } as ScrapersDto,
    {
      scrapers_id: 20,
      scraper_id: 20,
      scraper_name: 'LinkedIn Employees',
      site: 'linkedin.com/in',
      price: 0.5,
      campaign_type_id_campaign_type: 5,
    } as ScrapersDto,
  ],
};

export const useCampaignSourcesWithFallback = (id: string) => {
  const [skipQuery, setSkipQuery] = useState(false);

  const { data, error } = useGetCampaignSourcesQuery({ campaignTypeId: Number(id) || 0 } as any, {
    refetchOnMountOrArgChange: true,
    skip: id === null || id === 'undefined' || skipQuery,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    // Stop retrying on 404 or 500 errors
    if (error && typeof error === 'object' && 'status' in error) {
      const { status } = error as any;
      if (status === 404 || status === 500) {
        setSkipQuery(true);
      }
    }
  }, [error]);

  // Use fallback data if API call fails
  const fallbackData = useMemo(() => {
    const campaignTypeId = parseInt(id, 10);
    return MOCK_SCRAPERS[campaignTypeId] || [];
  }, [id]);

  // Return API data if available, otherwise use fallback
  const info = useMemo(() => {
    // Always use fallback for now since API might not be properly configured
    if (fallbackData && fallbackData.length > 0) {
      return fallbackData;
    }
    return data ?? [];
  }, [data, fallbackData]);

  return {
    data: info,
    isLoading: false, // Never show loading since we have fallback data
    error: null, // Never show error since we have fallback data
  };
};
